import { Check, Close } from "@mui/icons-material";
import { Box, IconButton, TextField, Typography } from "@mui/material";
import { keyframes, styled } from "@mui/material/styles";
import { onlineManager } from "@tanstack/react-query";
import { useNotification } from "app/NotificationProvider";
import { AnalyticsEvents, analytics } from "common/analytics";
import {
  StaticSuccessCheck,
  SuccessSparkle,
} from "common/components/SuccessSparkle";
import { ThumbsDownIcon, ThumbsUpIcon } from "common/icons";
import { useTranslation } from "common/locales";
import { isNetworkRequestError } from "common/utils/networkErrors";
import { isOfflineWriteBlockedError } from "common/utils/offlineWriteGuard";
import { EventType, Field, fieldHasComputedDryMatter } from "model";
import { useEffect, useRef, useState } from "react";
import {
  createOneReading,
  deleteAllTilesForField,
  invalidateFieldData,
} from "services";

const OFFLINE_MESSAGE = "You need to be online to use this.";

const FEEDBACK_STORAGE_KEY = "coverFeedbackDates";

function getTodayKey(): string {
  return new Date().toISOString().split("T")[0];
}

function hasGivenFeedbackToday(fieldGuid: string): boolean {
  try {
    const stored = localStorage.getItem(FEEDBACK_STORAGE_KEY);
    if (!stored) return false;
    const data = JSON.parse(stored);
    return data[fieldGuid] === getTodayKey();
  } catch {
    return false;
  }
}

function markFeedbackGiven(fieldGuid: string): void {
  try {
    const stored = localStorage.getItem(FEEDBACK_STORAGE_KEY);
    const data = stored ? JSON.parse(stored) : {};
    data[fieldGuid] = getTodayKey();
    localStorage.setItem(FEEDBACK_STORAGE_KEY, JSON.stringify(data));
  } catch {
    // Ignore storage errors
  }
}

type FeedbackState =
  | "initial"
  | "editing"
  | "submitted-ok"
  | "submitted-not-ok";

const fadeOutAnimation = keyframes`
  0% { opacity: 1; transform: scale(1); }
  100% { opacity: 0; transform: scale(0.8); }
`;

const AnimatedIcon = styled("div")<{
  $animateOut?: boolean;
}>(({ $animateOut }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  ...($animateOut && {
    animation: `${fadeOutAnimation} 0.2s ease-out forwards`,
  }),
}));

interface Props {
  field: Field;
  overgrazed?: boolean;
}

export default function TotalCoverFeedback({
  field,
  overgrazed = false,
}: Props) {
  const [feedbackState, setFeedbackState] = useState<FeedbackState>("initial");
  const [alreadyGaveFeedback, setAlreadyGaveFeedback] = useState(false);
  const [coverValue, setCoverValue] = useState<number>(field.dryMatterNow);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);
  const [showResultIcon, setShowResultIcon] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();
  const { notify } = useNotification();
  const hasComputedCover = fieldHasComputedDryMatter(field);

  useEffect(() => {
    // Reset feedback state when field changes
    setAlreadyGaveFeedback(hasGivenFeedbackToday(field.guid));
    setFeedbackState("initial");
    setShowResultIcon(false);
    setIsAnimatingOut(false);
  }, [field.guid]);

  useEffect(() => {
    setCoverValue(field.dryMatterNow);
  }, [field.dryMatterNow]);

  useEffect(() => {
    if (feedbackState === "editing" && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [feedbackState]);

  function notifySubmitError(error: unknown) {
    if (isOfflineWriteBlockedError(error)) return;
    if (isNetworkRequestError(error) || !onlineManager.isOnline()) {
      notify(OFFLINE_MESSAGE, "error");
      return;
    }
    notify(t("addCovers.failure"), "error");
  }

  const handleThumbsUp = () => {
    if (!hasComputedCover) return;
    if (alreadyGaveFeedback) return;
    if (!onlineManager.isOnline()) {
      notify(OFFLINE_MESSAGE, "error");
      return;
    }

    setIsAnimatingOut(true);

    setTimeout(async () => {
      const curDate = new Date();
      const timeDiff = 5 * 60000;

      try {
        await createOneReading({
          guid: "new",
          manualDryMatterInput: {
            dryMatterValue: field.dryMatterNow,
            field: field.guid,
            dateTimeMeasurementStart: curDate.toISOString(),
            dateTimeMeasurementEnd: new Date(
              curDate.getTime() + timeDiff
            ).toISOString(),
            event: { eventType: EventType.None },
            kind: "user",
            verificationStatus: "Accepted",
          },
        });
        analytics.track(AnalyticsEvents.COVER_READING_SUBMITTED, {
          method: "confirm",
          direction: "up",
        });
        markFeedbackGiven(field.guid);
        setAlreadyGaveFeedback(true);
        setIsAnimatingOut(false);
        setShowResultIcon(true);
        setFeedbackState("submitted-ok");
      } catch (e) {
        setIsAnimatingOut(false);
        setShowResultIcon(false);
        setFeedbackState("initial");
        notifySubmitError(e);
      }
    }, 200);
  };

  const handleThumbsDown = () => {
    if (!hasComputedCover) return;
    if (alreadyGaveFeedback) return;

    setFeedbackState("editing");
  };

  const handleSubmitCorrectedCover = () => {
    if (!coverValue) return;
    if (!onlineManager.isOnline()) {
      notify(OFFLINE_MESSAGE, "error");
      return;
    }

    setIsAnimatingOut(true);

    setTimeout(async () => {
      const curDate = new Date();
      const timeDiff = 5 * 60000;

      try {
        await createOneReading({
          guid: "new",
          manualDryMatterInput: {
            dryMatterValue: coverValue,
            field: field.guid,
            dateTimeMeasurementStart: curDate.toISOString(),
            dateTimeMeasurementEnd: new Date(
              curDate.getTime() + timeDiff
            ).toISOString(),
            event: { eventType: EventType.None },
            kind: "user",
            verificationStatus: "Accepted",
          },
        });

        await deleteAllTilesForField(field.guid);
        invalidateFieldData(field.guid);
        analytics.track(AnalyticsEvents.COVER_READING_SUBMITTED, {
          method: "adjust",
          direction: "down",
        });
        markFeedbackGiven(field.guid);
        setAlreadyGaveFeedback(true);
        setIsAnimatingOut(false);
        setShowResultIcon(true);
        setFeedbackState("submitted-not-ok");
      } catch (e) {
        setIsAnimatingOut(false);
        setShowResultIcon(false);
        setFeedbackState("editing");
        notifySubmitError(e);
      }
    }, 200);
  };

  const handleCancelEdit = () => {
    setFeedbackState("initial");
    setCoverValue(field.dryMatterNow);
    analytics.track(AnalyticsEvents.COVER_READING_CANCELLED, {
      direction: "down",
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSubmitCorrectedCover();
    } else if (e.key === "Escape") {
      handleCancelEdit();
    }
  };

  // Render based on feedback state
  const renderContent = () => {
    if (!hasComputedCover) {
      return (
        <>
          <Box>
            <Typography
              variant="caption"
              sx={{ color: "#2E7831", display: "block", fontSize: "0.7rem" }}
            >
              {t("fieldDetail.totalCover")}
            </Typography>
            <Typography
              variant="body1"
              sx={{ fontWeight: 600, color: "#1a1a2e" }}
            >
              {t("farm.cover.computing")}
            </Typography>
          </Box>
          <Typography
            variant="body2"
            sx={{ color: "#2E7831", fontSize: "0.75rem" }}
          >
            {t("fieldDetail.coverComputing")}
          </Typography>
        </>
      );
    }

    if (
      feedbackState === "submitted-ok" ||
      feedbackState === "submitted-not-ok"
    ) {
      return (
        <>
          <Box>
            <Typography
              variant="caption"
              sx={{ color: "#2E7831", display: "block", fontSize: "0.7rem" }}
            >
              {t("fieldDetail.totalCover")}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
              <Typography
                variant="body1"
                sx={{ fontWeight: 600, color: "#1a1a2e" }}
              >
                {feedbackState === "submitted-not-ok"
                  ? coverValue
                  : field.dryMatterNow}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "#1a1a2e", fontSize: "0.7rem" }}
              >
                kgDM/ha
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              variant="body2"
              sx={{ color: "#2E7831", fontSize: "0.75rem" }}
            >
              {t("fieldDetail.coverFeedback.thankYou")}
            </Typography>
            <Box sx={{ display: "flex", gap: 0.5 }}>
              {showResultIcon && (
                <SuccessSparkle
                  sparkles={feedbackState === "submitted-ok"}
                  pulse={feedbackState === "submitted-ok"}
                />
              )}
            </Box>
          </Box>
        </>
      );
    }

    if (feedbackState === "editing") {
      return (
        <>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box>
              <Typography
                variant="caption"
                sx={{ color: "#2E7831", display: "block", fontSize: "0.7rem" }}
              >
                {t("fieldDetail.totalCover")}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <TextField
                  inputRef={inputRef}
                  variant="standard"
                  value={coverValue ?? ""}
                  type="number"
                  onChange={(e) => setCoverValue(Number(e.target.value))}
                  onKeyDown={handleKeyDown}
                  sx={{ width: "auto" }}
                  slotProps={{
                    input: {
                      sx: {
                        fontWeight: 600,
                        color: "#1a1a2e",
                        fontSize: "1rem",
                        "& input": {
                          padding: 0,
                          width: "70px",
                          textAlign: "left",
                        },
                        "&:before": {
                          borderBottomColor: "#AFE57F",
                        },
                        "&:after": {
                          borderBottomColor: "#2E7831",
                        },
                      },
                    },
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{ color: "#1a1a2e", fontSize: "0.7rem" }}
                >
                  kgDM/ha
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: "flex", gap: 0.5 }}>
              <IconButton
                onClick={handleCancelEdit}
                size="small"
                sx={{
                  color: "#9CA3AF",
                  "&:hover": {
                    backgroundColor: "rgba(156, 163, 175, 0.1)",
                  },
                }}
              >
                <Close fontSize="small" />
              </IconButton>
              <IconButton
                onClick={handleSubmitCorrectedCover}
                disabled={!coverValue}
                size="small"
                sx={{
                  color: "#2E7831",
                  "&:hover": {
                    backgroundColor: "rgba(28, 67, 33, 0.1)",
                  },
                  "&.Mui-disabled": {
                    color: "#9CA3AF",
                  },
                }}
              >
                <Check fontSize="small" />
              </IconButton>
            </Box>
          </Box>
          <Box />
        </>
      );
    }

    // Initial state
    // Use warning colors when overgrazed
    const labelColor = overgrazed ? "#B45309" : "#2E7831"; // amber.700 vs green
    const iconColor = overgrazed ? "#B45309" : "#1C4321";
    const hoverBg = overgrazed
      ? "rgba(180, 83, 9, 0.1)"
      : "rgba(28, 67, 33, 0.1)";

    // If user already gave feedback (from localStorage), show thank you state
    if (alreadyGaveFeedback) {
      return (
        <>
          <Box>
            {overgrazed && (
              <Typography
                variant="caption"
                sx={{
                  color: "#B45309",
                  fontWeight: 600,
                  fontSize: "0.65rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  display: "block",
                  mb: 0.25,
                }}
              >
                {t("fieldview.overgrazed")}
              </Typography>
            )}
            <Typography
              variant="caption"
              sx={{ color: labelColor, display: "block", fontSize: "0.7rem" }}
            >
              {t("fieldDetail.totalCover")}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
              <Typography
                variant="body1"
                sx={{ fontWeight: 600, color: "#1a1a2e" }}
              >
                {field && !field.invalidatedTimestamp
                  ? field.dryMatterNow
                  : "—"}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "#1a1a2e", fontSize: "0.7rem" }}
              >
                kgDM/ha
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              variant="body2"
              sx={{ color: labelColor, fontSize: "0.75rem" }}
            >
              {t("fieldDetail.coverFeedback.thankYou")}
            </Typography>
            <StaticSuccessCheck />
          </Box>
        </>
      );
    }

    return (
      <>
        <Box>
          {overgrazed && (
            <Typography
              variant="caption"
              sx={{
                color: "#B45309",
                fontWeight: 600,
                fontSize: "0.65rem",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                display: "block",
                mb: 0.25,
              }}
            >
              {t("fieldview.overgrazed")}
            </Typography>
          )}
          <Typography
            variant="caption"
            sx={{ color: labelColor, display: "block", fontSize: "0.7rem" }}
          >
            {t("fieldDetail.totalCover")}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
            <Typography
              variant="body1"
              sx={{ fontWeight: 600, color: "#1a1a2e" }}
            >
              {field && !field.invalidatedTimestamp ? field.dryMatterNow : "—"}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "#1a1a2e", fontSize: "0.7rem" }}
            >
              kgDM/ha
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            variant="body2"
            sx={{ color: labelColor, fontSize: "0.75rem" }}
          >
            {t("fieldDetail.coverFeedback")}
          </Typography>
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <AnimatedIcon $animateOut={isAnimatingOut}>
              <IconButton
                onClick={handleThumbsUp}
                size="small"
                sx={{
                  "&:hover": {
                    backgroundColor: hoverBg,
                  },
                }}
              >
                <ThumbsUpIcon htmlColor={iconColor} fontSize="small" />
              </IconButton>
            </AnimatedIcon>
            <AnimatedIcon $animateOut={isAnimatingOut}>
              <IconButton
                onClick={handleThumbsDown}
                size="small"
                sx={{
                  "&:hover": {
                    backgroundColor: hoverBg,
                  },
                }}
              >
                <ThumbsDownIcon htmlColor={iconColor} fontSize="small" />
              </IconButton>
            </AnimatedIcon>
          </Box>
        </Box>
      </>
    );
  };

  // Daisy warning colors from theme
  const warningBg = "#FFFBEB"; // daisy.50 equivalent
  const warningBorder = "#F59E0B"; // daisy.500 equivalent
  const normalBg = "#F7FCF1";
  const normalBorder = "#AFE57F";

  return (
    <Box
      sx={{
        backgroundColor: overgrazed ? warningBg : normalBg,
        border: `1px solid ${overgrazed ? warningBorder : normalBorder}`,
        borderRadius: 2,
        px: 1.5,
        py: 1,
        mb: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        minHeight: 56,
      }}
    >
      {renderContent()}
    </Box>
  );
}
